import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

import type { SkillEditorSaveChange } from "../useSkillEditor";

export interface SkillPublishDialogProps {
    open: boolean;
    skillName: string;
    changes: readonly SkillEditorSaveChange[];
    isSaving: boolean;
    isBlocked: boolean;
    blockDescriptionId?: string;
    hasError: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => void;
}

export function SkillPublishDialog({
    open,
    skillName,
    changes,
    isSaving,
    isBlocked,
    blockDescriptionId,
    hasError,
    onOpenChange,
    onConfirm,
}: SkillPublishDialogProps) {
    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next && isSaving) return;
                onOpenChange(next);
            }}
        >
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Publish skill?</DialogTitle>
                    <DialogDescription>
                        Publish &quot;{skillName}&quot;? AI Chat will use this
                        version immediately.
                    </DialogDescription>
                </DialogHeader>
                {changes.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                        No content changes; publishing will publish the saved
                        draft.
                    </p>
                ) : (
                    <div className="space-y-1.5">
                        <p className="text-sm font-medium">
                            Changes in this save
                        </p>
                        <ul className="text-muted-foreground space-y-1 text-sm">
                            {changes.map(({ id, label }) => (
                                <li key={id}>{label}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {hasError && (
                    <p role="alert" className="text-destructive text-sm">
                        Failed to publish. Try again.
                    </p>
                )}
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={onConfirm}
                        aria-describedby={blockDescriptionId}
                        disabled={isSaving || isBlocked}
                    >
                        {isSaving ? "Publishing…" : "Publish"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
